import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AdminLayout from '@/components/layout/AdminLayout'
import ItemFormWizard from '@/components/admin/ItemFormWizard'
import { adminAPI } from '@/lib/api/admin'
import { useAppConfig } from '@/hooks/useAppConfig'

export default function AdminDashboardPage() {
  const navigate = useNavigate()
  const { config } = useAppConfig()
  const mockMode = config.nfcMockMode
  const [activeTab, setActiveTab] = useState<'Add Item' | 'NFC Tags' | 'Manage'>('Add Item')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const [mediaUploadKey, setMediaUploadKey] = useState(0)
  const [createdListing, setCreatedListing] = useState<{ id: string; title: string } | null>(null)
  const [publishing, setPublishing] = useState(false)
  const [published, setPublished] = useState(false)

  // In mock mode the NFC Tags tab is meaningless (no hardware) — hide it entirely.
  const tabs: Array<'Add Item' | 'NFC Tags' | 'Manage'> = mockMode
    ? ['Add Item', 'Manage']
    : ['Add Item', 'NFC Tags', 'Manage']

  const handleFormSubmit = async (data: any) => {
    setLoading(true)
    setError('')
    setSuccess('')
    setCreatedListing(null)
    setPublished(false)

    try {
      console.log('Creating listing with data:', data)
      const response = await adminAPI.createListing(data)
      console.log('Listing created successfully:', response)

      setCreatedListing({ id: response.id, title: response.title })
      setSuccess(`Listing "${response.title}" created successfully! (ID: ${response.id})`)

      // Reset form by incrementing key
      setMediaUploadKey(prev => prev + 1)

      // Scroll to success message
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      console.error('Failed to create listing:', err)
      setError(err instanceof Error ? err.message : 'Failed to create listing')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } finally {
      setLoading(false)
    }
  }

  const handlePublish = async () => {
    if (!createdListing) return
    setPublishing(true)
    setError('')

    try {
      await adminAPI.mockProvisionListing(createdListing.id)
      setPublished(true)
      setSuccess(`"${createdListing.title}" is now LIVE and open for bidding.`)
    } catch (err) {
      console.error('Failed to publish listing:', err)
      setError(err instanceof Error ? err.message : 'Failed to publish listing')
    } finally {
      setPublishing(false)
    }
  }

  const handleMediaUploaded = (_mediaIds: string[]) => {
    // This is now handled by the wizard
  }

  const handleLogout = () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    navigate('/admin/login')
  }

  return (
    <AdminLayout onLogout={handleLogout}>
      <div className="min-h-screen" style={{ backgroundColor: 'var(--background-secondary)' }}>

        <div className="max-w-7xl mx-auto" style={{ padding: 'var(--spacing-2xl)' }}>
          {/* Hero Section with Metrics */}
          <div className="hero-gradient card shimmer" style={{ padding: 'var(--spacing-2xl)', marginBottom: 'var(--spacing-2xl)' }}>
            <div className="text-center" style={{ marginBottom: 'var(--spacing-xl)' }}>
              <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto floating-animation pulse-glow" style={{
                background: 'linear-gradient(135deg, var(--brand-primary) 0%, #0a5230 100%)',
                marginBottom: 'var(--spacing-lg)'
              }}>
                <svg className="w-10 h-10" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                </svg>
              </div>
              <h1 className="text-4xl font-bold gradient-text" style={{ marginBottom: 'var(--spacing-sm)' }}>
                Admin Dashboard
              </h1>
              <p className="text-lg" style={{ color: 'var(--text-secondary)', opacity: 0.8 }}>
                Multi-Sport Item Management & NFC Platform
              </p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="metric-card" style={{
                padding: 'var(--spacing-lg)',
                borderRadius: 'var(--radius-lg)',
                textAlign: 'center'
              }}>
                <div className="text-2xl font-bold gradient-text">247</div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Total Items</div>
              </div>
              <div className="metric-card" style={{
                padding: 'var(--spacing-lg)',
                borderRadius: 'var(--radius-lg)',
                textAlign: 'center'
              }}>
                <div className="text-2xl font-bold" style={{ color: 'var(--accent-success)' }}>98%</div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>NFC Success</div>
              </div>
              <div className="metric-card" style={{
                padding: 'var(--spacing-lg)',
                borderRadius: 'var(--radius-lg)',
                textAlign: 'center'
              }}>
                <div className="text-2xl font-bold" style={{ color: 'var(--brand-primary)' }}>156</div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Live Items</div>
              </div>
              <div className="metric-card" style={{
                padding: 'var(--spacing-lg)',
                borderRadius: 'var(--radius-lg)',
                textAlign: 'center'
              }}>
                <div className="text-2xl font-bold" style={{ color: 'var(--accent-warning)' }}>23</div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Pending</div>
              </div>
            </div>
          </div>


          {/* Premium Tab Navigation */}
          <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
            <div style={{ padding: 'var(--spacing-md)' }}>
              <nav className="flex" style={{ gap: 'var(--spacing-sm)' }}>
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab as any)}
                    className="font-medium text-sm transition-all duration-300"
                    style={{
                      padding: 'var(--spacing-md) var(--spacing-lg)',
                      borderRadius: 'var(--radius-md)',
                      background: activeTab === tab
                        ? 'linear-gradient(135deg, var(--brand-primary) 0%, #0a5230 100%)'
                        : 'transparent',
                      color: activeTab === tab ? 'white' : 'var(--text-muted)',
                      boxShadow: activeTab === tab
                        ? '0 4px 15px rgba(11, 77, 42, 0.3)'
                        : '0 2px 8px rgba(0, 0, 0, 0.02)',
                      transform: activeTab === tab ? 'translateY(-1px)' : 'translateY(0)'
                    }}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div style={{ padding: 'var(--spacing-xl)' }}>
              {activeTab === 'Add Item' && (
                <div>
                  {/* Error Message */}
                  {error && (
                    <div className="mb-8 shimmer" style={{
                      padding: 'var(--spacing-lg)',
                      background: 'linear-gradient(135deg, rgba(225, 29, 72, 0.1) 0%, rgba(225, 29, 72, 0.05) 100%)',
                      border: '1px solid rgba(225, 29, 72, 0.2)',
                      borderRadius: 'var(--radius-lg)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: '0 4px 20px rgba(225, 29, 72, 0.15)'
                    }}>
                      <div className="flex items-center" style={{ gap: 'var(--spacing-md)' }}>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
                          background: 'var(--accent-danger)',
                          color: 'white'
                        }}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </div>
                        <p className="font-medium" style={{ color: 'var(--accent-danger)' }}>{error}</p>
                      </div>
                    </div>
                  )}

                  {/* Success Message */}
                  {success && (
                    <div className="mb-8 shimmer" style={{
                      padding: 'var(--spacing-lg)',
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      borderRadius: 'var(--radius-lg)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: '0 4px 20px rgba(16, 185, 129, 0.15)'
                    }}>
                      <div className="flex items-center" style={{ gap: 'var(--spacing-md)' }}>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
                          background: 'var(--accent-success)',
                          color: 'white'
                        }}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <p className="font-medium" style={{ color: 'var(--accent-success)' }}>{success}</p>
                      </div>
                    </div>
                  )}

                  {/* One-click publish (mock mode): take the just-created DRAFT listing
                      straight to LIVE with no NFC/e-certificate step. */}
                  {mockMode && createdListing && (
                    <div className="mb-8" style={{
                      padding: 'var(--spacing-lg)',
                      background: 'var(--background-secondary)',
                      border: '1px solid rgba(11, 77, 42, 0.2)',
                      borderRadius: 'var(--radius-lg)'
                    }}>
                      {!published ? (
                        <div className="flex items-center justify-between" style={{ gap: 'var(--spacing-md)' }}>
                          <div>
                            <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                              Ready to publish: {createdListing.title}
                            </p>
                            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                              Publishes straight to LIVE and opens an auction — no NFC step needed.
                            </p>
                          </div>
                          <button
                            onClick={handlePublish}
                            disabled={publishing}
                            className="font-semibold text-sm transition-all duration-300"
                            style={{
                              padding: 'var(--spacing-md) var(--spacing-lg)',
                              borderRadius: 'var(--radius-md)',
                              background: publishing
                                ? 'var(--text-muted)'
                                : 'linear-gradient(135deg, var(--brand-primary) 0%, #0a5230 100%)',
                              color: 'white',
                              whiteSpace: 'nowrap',
                              cursor: publishing ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {publishing ? 'Publishing…' : 'Publish to LIVE'}
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between" style={{ gap: 'var(--spacing-md)' }}>
                          <p className="font-semibold" style={{ color: 'var(--accent-success)' }}>
                            ✓ Live and open for bidding
                          </p>
                          <div className="flex" style={{ gap: 'var(--spacing-sm)' }}>
                            <a
                              href={`/verify?listingId=${createdListing.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="font-semibold text-sm"
                              style={{
                                padding: 'var(--spacing-md) var(--spacing-lg)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--brand-primary)',
                                color: 'var(--brand-primary)'
                              }}
                            >
                              Verify authenticity
                            </a>
                            <button
                              onClick={() => navigate('/marketplace')}
                              className="font-semibold text-sm"
                              style={{
                                padding: 'var(--spacing-md) var(--spacing-lg)',
                                borderRadius: 'var(--radius-md)',
                                background: 'linear-gradient(135deg, var(--brand-primary) 0%, #0a5230 100%)',
                                color: 'white'
                              }}
                            >
                              View in marketplace
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Item Form Wizard */}
                  <ItemFormWizard
                    key={mediaUploadKey}
                    onSubmit={handleFormSubmit}
                    loading={loading}
                    mediaUploadKey={mediaUploadKey}
                    onMediaUploaded={handleMediaUploaded}
                  />
                </div>
              )}

              {activeTab === 'NFC Tags' && (
                <div>
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold gradient-text mb-2">NFC Tag Management</h3>
                    <p style={{ color: 'var(--text-secondary)' }}>Write and manage NFC tags for your listings</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* USB NFC Writer */}
                    <button
                      onClick={() => navigate('/admin/nfc-writer')}
                      className="glass-card shimmer transition-all duration-300 hover:scale-105 group"
                      style={{
                        padding: 'var(--spacing-xl)',
                        textAlign: 'left',
                        position: 'relative'
                      }}
                    >
                      <div className="flex items-start" style={{ gap: 'var(--spacing-md)' }}>
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{
                          background: 'linear-gradient(135deg, var(--brand-primary) 0%, #0a5230 100%)',
                          boxShadow: '0 4px 15px rgba(11, 77, 42, 0.3)'
                        }}>
                          <svg className="w-7 h-7" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-bold text-lg gradient-text mb-1">USB NFC Writer</p>
                          <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                            Write NFC tags using a USB reader connected to your computer
                          </p>
                          <div className="flex items-center mt-2" style={{ gap: 'var(--spacing-xs)' }}>
                            <div className="w-2 h-2 rounded-full" style={{ background: 'var(--accent-success)' }}></div>
                            <span className="text-xs" style={{ color: 'var(--accent-success)' }}>Desktop</span>
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* Mobile NFC */}
                    <button
                      onClick={() => window.open('/mobile', '_blank')}
                      className="glass-card shimmer transition-all duration-300 hover:scale-105 group"
                      style={{
                        padding: 'var(--spacing-xl)',
                        textAlign: 'left',
                        position: 'relative'
                      }}
                    >
                      <div className="flex items-start" style={{ gap: 'var(--spacing-md)' }}>
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{
                          background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                          boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)'
                        }}>
                          <svg className="w-7 h-7" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-bold text-lg" style={{ color: '#8B5CF6' }}>Mobile NFC App</p>
                          <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                            Scan and bind NFC tags using your phone's built-in NFC
                          </p>
                          <div className="flex items-center mt-2" style={{ gap: 'var(--spacing-xs)' }}>
                            <div className="w-2 h-2 rounded-full" style={{ background: '#8B5CF6' }}></div>
                            <span className="text-xs" style={{ color: '#8B5CF6' }}>Mobile</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'Manage' && (
                <div>
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold gradient-text mb-2">Management Tools</h3>
                    <p style={{ color: 'var(--text-secondary)' }}>Advanced administrative functions and analytics</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <button
                      onClick={() => navigate('/admin/analytics')}
                      className="glass-card shimmer transition-all duration-300 hover:scale-105 group"
                      style={{
                        padding: 'var(--spacing-xl)',
                        textAlign: 'left',
                        position: 'relative'
                      }}
                    >
                      <div className="flex items-start" style={{ gap: 'var(--spacing-md)' }}>
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{
                          background: 'linear-gradient(135deg, var(--brand-primary) 0%, #0a5230 100%)',
                          boxShadow: '0 4px 15px rgba(11, 77, 42, 0.3)'
                        }}>
                          <svg className="w-7 h-7" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-bold text-lg gradient-text mb-1">Analytics Dashboard</p>
                          <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                            View performance metrics, user engagement, and sales analytics
                          </p>
                          <div className="flex items-center mt-2" style={{ gap: 'var(--spacing-xs)' }}>
                            <div className="w-2 h-2 rounded-full" style={{ background: 'var(--accent-success)' }}></div>
                            <span className="text-xs" style={{ color: 'var(--accent-success)' }}>Live Data</span>
                          </div>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => navigate('/admin/bulk-import')}
                      className="glass-card shimmer transition-all duration-300 hover:scale-105 group"
                      style={{
                        padding: 'var(--spacing-xl)',
                        textAlign: 'left',
                        position: 'relative'
                      }}
                    >
                      <div className="flex items-start" style={{ gap: 'var(--spacing-md)' }}>
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{
                          background: 'linear-gradient(135deg, var(--accent-success) 0%, #059669 100%)',
                          boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
                        }}>
                          <svg className="w-7 h-7" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-bold text-lg" style={{ color: 'var(--accent-success)' }}>Bulk Import</p>
                          <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                            Import multiple items from CSV or Excel files
                          </p>
                          <div className="flex items-center mt-2" style={{ gap: 'var(--spacing-xs)' }}>
                            <div className="w-2 h-2 rounded-full" style={{ background: 'var(--accent-warning)' }}></div>
                            <span className="text-xs" style={{ color: 'var(--accent-warning)' }}>Beta Feature</span>
                          </div>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => navigate('/marketplace')}
                      className="glass-card shimmer transition-all duration-300 hover:scale-105 group"
                      style={{
                        padding: 'var(--spacing-xl)',
                        textAlign: 'left',
                        position: 'relative'
                      }}
                    >
                      <div className="flex items-start" style={{ gap: 'var(--spacing-md)' }}>
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{
                          background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                          boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)'
                        }}>
                          <svg className="w-7 h-7" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-bold text-lg" style={{ color: '#8B5CF6' }}>View Marketplace</p>
                          <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                            Preview how your items appear to customers
                          </p>
                          <div className="flex items-center mt-2" style={{ gap: 'var(--spacing-xs)' }}>
                            <div className="w-2 h-2 rounded-full" style={{ background: 'var(--brand-primary)' }}></div>
                            <span className="text-xs" style={{ color: 'var(--brand-primary)' }}>Public View</span>
                          </div>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => navigate('/admin/payment-test')}
                      className="glass-card shimmer transition-all duration-300 hover:scale-105 group"
                      style={{
                        padding: 'var(--spacing-xl)',
                        textAlign: 'left',
                        position: 'relative'
                      }}
                    >
                      <div className="flex items-start" style={{ gap: 'var(--spacing-md)' }}>
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{
                          background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                          boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
                        }}>
                          <svg className="w-7 h-7" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-bold text-lg" style={{ color: '#10B981' }}>Payment Test</p>
                          <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                            Test Stripe payment integration
                          </p>
                          <div className="flex items-center mt-2" style={{ gap: 'var(--spacing-xs)' }}>
                            <div className="w-2 h-2 rounded-full" style={{ background: '#EF4444' }}></div>
                            <span className="text-xs" style={{ color: '#EF4444' }}>Dev Tool</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
